import type { JobAnalytics, JobPosting, JobStatus } from '@/types';
import { simulate, uid } from '@/services/api/client';
import { scopedCall } from '@/services/company/companyScope';

const JOBS: JobPosting[] = [
  {
    id: 'job_1', companyId: 'co_demo', title: 'Senior Backend Engineer', department: 'Engineering',
    description: 'Own core services and platform reliability.', responsibilities: ['Design distributed services', 'Mentor engineers'],
    requirements: ['5+ years backend', 'Go or Rust'], benefits: ['Equity', 'Remote-first'], location: 'Remote', remote: true,
    type: 'full_time', experienceLevel: 'senior', salaryMin: 160000, salaryMax: 210000, currency: 'USD',
    status: 'open', urgent: true, postedAt: new Date(Date.now() - 7 * 86400000).toISOString(), views: 1240, applicationCount: 38,
  },
  {
    id: 'job_2', companyId: 'co_demo', title: 'Product Designer', department: 'Design',
    description: 'Shape the next chapter of our product.', responsibilities: ['Lead end-to-end design'],
    requirements: ['Portfolio', 'Systems thinking'], benefits: ['Health', 'PTO'], location: 'San Francisco, US', remote: false,
    type: 'full_time', experienceLevel: 'mid', salaryMin: 130000, salaryMax: 165000, currency: 'USD',
    status: 'open', postedAt: new Date(Date.now() - 14 * 86400000).toISOString(), views: 820, applicationCount: 22,
  },
  {
    id: 'job_3', companyId: 'co_demo', title: 'Growth Marketer', department: 'Marketing',
    description: 'Lead paid + organic growth experiments.', responsibilities: ['Run growth experiments'],
    requirements: ['B2B SaaS experience'], benefits: ['Bonus'], location: 'London, UK', remote: true,
    type: 'full_time', experienceLevel: 'mid', salaryMin: 95000, salaryMax: 130000, currency: 'GBP',
    status: 'paused', postedAt: new Date(Date.now() - 30 * 86400000).toISOString(), views: 410, applicationCount: 9,
  },
];

export type BulkJobAction = 'urgent' | 'unurgent' | 'pause' | 'resume' | 'archive' | 'delete';
export interface BulkJobResult { success: boolean; processed: number; skipped: number; action: BulkJobAction }

export const jobsService = {
  // All job operations route through the endeeko recruitment callables; mock-first.
  list: (filter?: { status?: JobStatus }) =>
    scopedCall<JobPosting[]>('listJobs', { status: filter?.status }, () =>
      simulate(filter?.status ? JOBS.filter((j) => j.status === filter.status) : JOBS)),
  getById: (id: string) =>
    scopedCall<JobPosting | null>('getJob', { jobId: id }, () => simulate(JOBS.find((j) => j.id === id) ?? null)),
  /** `expiresInDays` / `urgentDays` are turned into `expiresAt` / `urgentUntil` by the server. */
  create: (input: Omit<JobPosting, 'id' | 'postedAt' | 'views' | 'applicationCount'> & { expiresInDays?: number; urgentDays?: number }) => {
    const local = () => {
      const job: JobPosting = { ...input, id: uid('job'), postedAt: new Date().toISOString(), views: 0, applicationCount: 0 };
      JOBS.unshift(job);
      return simulate(job);
    };
    return scopedCall<JobPosting>('createJob', { job: input }, local);
  },
  update: (id: string, patch: Partial<JobPosting>) => {
    const local = () => {
      const idx = JOBS.findIndex((j) => j.id === id);
      if (idx >= 0) JOBS[idx] = { ...JOBS[idx], ...patch };
      return simulate(JOBS[idx] ?? null);
    };
    return scopedCall<JobPosting | null>('updateJob', { jobId: id, updates: patch }, local);
  },
  remove: (id: string) => {
    const local = () => {
      const idx = JOBS.findIndex((j) => j.id === id);
      if (idx >= 0) JOBS.splice(idx, 1);
      return simulate(true);
    };
    return scopedCall<boolean>('deleteJob', { jobId: id }, local);
  },
  /** Urgent (7/14/30 days) / unurgent / pause / resume / archive / delete over one or many jobs. */
  bulk: (ids: string[], action: BulkJobAction, durationDays: 7 | 14 | 30 = 7) => {
    const local = () => {
      let processed = 0;
      const until = new Date(Date.now() + durationDays * 86400000).toISOString();
      for (const id of ids) {
        const idx = JOBS.findIndex((j) => j.id === id);
        if (idx < 0) continue;
        const j = JOBS[idx];
        if (action === 'delete') { JOBS.splice(idx, 1); processed++; continue; }
        const patch: Partial<JobPosting> | null =
          action === 'urgent' ? { urgent: true, urgentUntil: until }
          : action === 'unurgent' ? { urgent: false, urgentUntil: null }
          : action === 'pause' ? (j.status === 'open' ? { status: 'paused' } : null)
          : action === 'resume' ? (j.status === 'paused' ? { status: 'open' } : null)
          : j.status !== 'archived' ? { status: 'archived', urgent: false, urgentUntil: null } : null;
        if (patch) { JOBS[idx] = { ...j, ...patch }; processed++; }
      }
      return simulate<BulkJobResult>({ success: true, processed, skipped: ids.length - processed, action }, 250);
    };
    return scopedCall<BulkJobResult>('bulkJobAction', { jobIds: ids, action, durationDays }, local);
  },
  analytics: (id: string): Promise<JobAnalytics> =>
    scopedCall<Partial<JobAnalytics>>('getJobAnalytics', { jobId: id }, () => simulate<Partial<JobAnalytics>>({
      jobId: id,
      views: 1240,
      applications: 38,
      averageTimeToApplyMinutes: 14,
      topSources: [
        { source: 'LinkedIn', count: 410 }, { source: 'Direct', count: 320 }, { source: 'Indeed', count: 180 }, { source: 'Referrals', count: 95 },
      ],
      funnel: [
        { stage: 'Viewed', count: 1240 }, { stage: 'Started', count: 210 }, { stage: 'Applied', count: 38 },
        { stage: 'Screened', count: 22 }, { stage: 'Interviewed', count: 9 }, { stage: 'Offered', count: 2 },
      ],
    })).then((a): JobAnalytics => ({
      // The backend returns views / applications / funnel only — fill the rest so pages never read undefined.
      jobId: a.jobId ?? id,
      views: a.views ?? 0,
      applications: a.applications ?? 0,
      averageTimeToApplyMinutes: a.averageTimeToApplyMinutes ?? 0,
      topSources: Array.isArray(a.topSources) ? a.topSources : [],
      funnel: Array.isArray(a.funnel) ? a.funnel : [],
    })),
};
